import type { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Navigation state marking a history entry as pushed by an in-app list.
 * Producers build it with `backToHereState`; `useInAppBack` consumes it, and
 * detail pages read `backLink`/`backLabel` for their fallback link and label.
 */
export interface InAppBackState {
  backLink: string;
  backLabel: string;
}

/**
 * Back-state pointing at the given location (whose search params carry the
 * list's view state) — pass as `state` when pushing a detail page so its back
 * arrow can pop straight back to this exact view.
 */
export function backToHereState(
  location: { pathname: string; search: string },
  backLabel: string
): InAppBackState {
  return { backLink: location.pathname + location.search, backLabel };
}

/**
 * When the current history entry was pushed in-app (it carries a `backLink`
 * in navigation state), back affordances should POP that entry rather than
 * push the list URL again — pushing grows history as [list, detail, list, …],
 * which makes the browser back button land on the detail page the user just
 * left. Returns a Link onClick handler that pops on plain left-clicks while
 * letting modified clicks (cmd/ctrl/shift/alt — open in new tab/window) fall
 * through to the anchor's href, or null for deep links / new tabs (no
 * navigation state), where the plain `<Link to>` behavior is already right.
 */
export function useInAppBack():
  | ((event: MouseEvent<HTMLAnchorElement>) => void)
  | null {
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromApp = Boolean(
    (location.state as { backLink?: string } | null)?.backLink
  );
  if (!cameFromApp) return null;
  return event => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    navigate(-1);
  };
}
